import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster, toast } from "sonner";
import "@/App.css";
import { api } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import Home from "@/pages/Home";
import World from "@/pages/World";
import Progress from "@/pages/Progress";
import Admin from "@/pages/Admin";
import DrumPractice from "@/pages/DrumPractice";
import EnglishQuest from "@/pages/EnglishQuest";
import TestMode from "@/pages/TestMode";
import CelebrationModal from "@/components/CelebrationModal";
import BadgeImage from "@/components/BadgeImage";

function Shell() {
  const location = useLocation();
  const [state, setState] = useState(null);
  const [perfectWeek, setPerfectWeek] = useState(false);
  const [newUnlock, setNewUnlock] = useState(null);
  const [newReward, setNewReward] = useState(null);
  const [levelUp, setLevelUp] = useState(null);
  const [rewardSheet, setRewardSheet] = useState(null);
  const [achievement, setAchievement] = useState(null);

  const reload = useCallback(async () => {
    const r = await api.get("/state");
    setState(r.data);
    return r.data;
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onCompleted = useCallback(async (newItems) => {
    const prev = state;
    const next = await reload();
    for (const item of newItems || []) {
      if (item.kind === "perfect_week") setPerfectWeek(true);
      else if (item.kind === "unlock") setNewUnlock(item);
      else if (item.kind === "reward") setNewReward(item);
      else if (item.kind === "achievement") {
        const name = achNameFor(item.code, next);
        setAchievement({ code: item.code, name });
        toast.success(`Achievement: ${name}`);
      }
    }
    if (prev && next && prev.level.level < next.level.level) {
      setLevelUp(next.level.level);
    }
  }, [reload, state]);

  const showNav = !location.pathname.startsWith("/drum") && !location.pathname.startsWith("/admin");

  if (!state && !location.pathname.startsWith("/admin")) {
    return <div className="min-h-dvh flex items-center justify-center text-[hsl(var(--ink-soft))]">Loading…</div>;
  }

  return (
    <div className="min-h-dvh text-foreground">
      <Toaster position="top-center" richColors toastOptions={{ className: "font-semibold" }}/>
      <div className="max-w-md mx-auto md:max-w-3xl lg:max-w-6xl px-5 lg:px-8">
        <Routes>
          <Route path="/" element={state && <Home state={state} onOpenReward={() => setRewardSheet(state.rewards.find(r => r.status === "locked"))} />} />
          <Route path="/world" element={state && <World state={state} />} />
          <Route path="/progress" element={state && <Progress state={state} />} />
          <Route path="/english/:id" element={<EnglishQuest onCompleted={onCompleted} />} />
          <Route path="/drum" element={<DrumPractice onCompleted={onCompleted} />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/test" element={<TestMode />} />
        </Routes>
      </div>
      {showNav && <BottomNav />}

      <CelebrationModal
        open={perfectWeek}
        onClose={() => setPerfectWeek(false)}
        title="PERFECT WEEK!"
        subtitle="All 5 quests completed. +25 bonus XP"
        mascotState="celebrating"
        cta="Yay!"
        testid="perfect-week-modal"
      />
      <CelebrationModal
        open={!!newUnlock}
        onClose={() => setNewUnlock(null)}
        title="New item unlocked!"
        subtitle={newUnlock?.name}
        mascotState="happy"
        cta="See my room"
        testid="unlock-modal"
      />
      <CelebrationModal
        open={!!newReward}
        onClose={() => setNewReward(null)}
        title="MYSTERY REWARD UNLOCKED!"
        subtitle={`Ask Mum/Dad about your surprise.`}
        emoji="🎁"
        cta="Awesome!"
        testid="reward-unlock-modal"
      />
      <CelebrationModal
        open={!!levelUp}
        onClose={() => setLevelUp(null)}
        title={`Level ${levelUp}!`}
        subtitle="You just leveled up"
        mascotState="celebrating"
        cta="Keep going"
        testid="level-up-modal"
      />
      <CelebrationModal
        open={!!achievement}
        onClose={() => setAchievement(null)}
        title="Achievement unlocked!"
        subtitle={achievement?.name}
        customVisual={achievement ? <BadgeImage code={achievement.code} size={128} testId="achievement-modal-badge"/> : null}
        mascotState="achievement"
        cta="Nice!"
        testid="achievement-modal"
      />
      <CelebrationModal
        open={!!rewardSheet}
        onClose={() => setRewardSheet(null)}
        title={rewardSheet?.child_teaser || "Mystery reward"}
        subtitle={rewardSheet && progressBlurb(rewardSheet, state)}
        emoji="🎁"
        confetti={false}
        cta="Okay!"
        testid="reward-info-modal"
      />
    </div>
  );
}

function achNameFor(code, state) {
  return state?.achievements.all.find((a) => a.code === code)?.name || code;
}

function progressBlurb(reward, state) {
  const labels = { perfect_weeks: "Perfect Weeks", english_quests: "English quests", drum_sessions: "Drum practices", xp: "XP" };
  let cur = 0;
  if (reward.requirement_type === "perfect_weeks") cur = state.totals.perfect_weeks;
  else if (reward.requirement_type === "english_quests") cur = state.totals.english_completed;
  else if (reward.requirement_type === "drum_sessions") cur = state.totals.drum_qualified;
  else if (reward.requirement_type === "xp") cur = state.xp;
  const left = Math.max(0, reward.requirement_amount - cur);
  return `${left} more ${labels[reward.requirement_type]} to go.`;
}

export default function App() {
  return (
    <BrowserRouter>
      <Shell/>
    </BrowserRouter>
  );
}
